import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, GraduationCap, Medal, Plus, X } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { useMasters } from "../form/engine";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const TEACHERS = ["chef", "mentor", "admin"];
const inputCls = "w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60";

// FUNZ Academy — chefs & mentors publish courses, students enroll, lesson
// progress unlocks a verifiable certificate (FUNZ-XXXXXXXX).
export const AcademyPanel = () => {
  const { user } = useAuth();
  const lang = useLang();
  const masters = useMasters();
  const canTeach = TEACHERS.includes(user.role);
  const [tab, setTab] = useState("browse");
  const [courses, setCourses] = useState(null);
  const [mine, setMine] = useState([]);
  const [learning, setLearning] = useState([]);
  const [certs, setCerts] = useState([]);
  const [skill, setSkill] = useState("");
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ title: "", skill: "", level: "Beginner", price: "", lessons: [{ title: "", minutes: 10 }] });

  const load = () => {
    axios.get(`${API}/academy/courses?skill=${encodeURIComponent(skill)}`, headers()).then((r) => setCourses(r.data.courses)).catch(() => setCourses([]));
    axios.get(`${API}/academy/my`, headers()).then((r) => setLearning(r.data.enrollments)).catch(() => {});
    axios.get(`${API}/academy/certificates/mine`, headers()).then((r) => setCerts(r.data.certificates)).catch(() => {});
    if (canTeach) axios.get(`${API}/academy/courses/mine`, headers()).then((r) => setMine(r.data.courses)).catch(() => {});
  };
  useEffect(load, [skill]);

  const enroll = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/academy/courses/${id}/enroll`, {}, headers());
      toast.success("Enrolled — first lesson unlocked 🎓");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const toggleLesson = async (enrollId, index) => {
    try {
      const { data } = await axios.post(`${API}/academy/enrollments/${enrollId}/lesson`, { index }, headers());
      if (data.cert_code) toast.success(`🏅 Course complete! Certificate ${data.cert_code}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createCourse = async () => {
    setBusy("create");
    try {
      await axios.post(`${API}/academy/courses`, {
        ...form,
        price: Number(form.price) || 0,
        lessons: form.lessons.filter((l) => l.title.trim()).map((l) => ({ title: l.title.trim(), minutes: Number(l.minutes) || 10 })),
      }, headers());
      toast.success("Course published 🎓");
      setForm({ title: "", skill: "", level: "Beginner", price: "", lessons: [{ title: "", minutes: 10 }] });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const tabs = [
    { id: "browse", label: `🎓 ${t(lang, "ac_browse")}` },
    { id: "learn", label: `📖 ${t(lang, "ac_learn")}` },
    ...(canTeach ? [{ id: "teach", label: `🧑‍🏫 ${t(lang, "ac_teach")}` }] : []),
  ];

  return (
    <div className="space-y-5" data-testid="panel-academy">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`ac-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`rounded-full px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === tb.id ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "border border-white/10 text-slate-400 hover:text-white"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "browse" && (
        <>
          <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
            {["", ...(masters.course_skills || [])].map((s) => (
              <button key={s || "all"} data-testid={`ac-skill-${(s || "all").toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setSkill(s)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-bold ${skill === s ? "bg-[#7C3AED] text-white" : "border border-white/10 text-slate-400"}`}>
                {s || "All skills"}
              </button>
            ))}
          </div>
          {courses === null ? (
            <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
          ) : courses.length === 0 ? (
            <p className="text-sm text-slate-500">No courses yet{canTeach ? " — publish yours from Teach!" : " — chefs are cooking some up."}</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {courses.map((c) => (
                <div key={c.id} data-testid={`ac-course-${c.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold">{c.title}</p>
                      <p className="font-jbmono text-[10px] text-slate-500">
                        {c.instructor_name} · {c.skill} · {c.level} · {c.lesson_count} lessons · {c.minutes} min
                      </p>
                    </div>
                    <span className="shrink-0 font-jbmono text-xs font-bold text-[#22C55E]">{c.price ? `₹${c.price}` : "Free"}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="font-jbmono text-[10px] text-slate-500">🎓 {c.enrolled} enrolled</span>
                    {c.is_enrolled ? (
                      <span className="ml-auto rounded-full bg-[#06B6D4]/15 px-4 py-2 font-jbmono text-[10px] font-bold text-[#06B6D4] rtl:ml-0 rtl:mr-auto" data-testid={`ac-enrolled-${c.id.slice(-6)}`}>
                        ✓ {t(lang, "ac_enrolled")}
                      </span>
                    ) : (
                      <button data-testid={`ac-enroll-${c.id.slice(-6)}`} onClick={() => enroll(c.id)} disabled={busy === c.id}
                        className="ml-auto rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-2 font-display text-[10px] font-bold uppercase tracking-wider text-white disabled:opacity-50 rtl:ml-0 rtl:mr-auto">
                        {busy === c.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : t(lang, "ac_enroll")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "learn" && (
        <div className="space-y-4">
          {certs.length > 0 && (
            <div className="rounded-2xl border border-[#F97316]/30 bg-[#F97316]/[0.06] p-4" data-testid="ac-certs">
              <p className="flex items-center gap-1.5 font-display text-sm font-bold"><Medal className="h-4 w-4 text-[#F97316]" /> {t(lang, "ac_certificate")}</p>
              <div className="mt-2.5 space-y-1.5">
                {certs.map((c) => (
                  <p key={c.cert_code} className="font-jbmono text-[11px] text-slate-300" data-testid={`ac-cert-${c.cert_code}`}>
                    🏅 {c.course_title} — <span className="font-bold text-[#F97316]">{c.cert_code}</span>
                  </p>
                ))}
              </div>
            </div>
          )}
          {learning.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing in progress — enroll in a course.</p>
          ) : (
            learning.map((e) => (
              <div key={e.id} data-testid={`ac-learning-${e.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{e.course_title}</p>
                    <p className="font-jbmono text-[10px] text-slate-500">{e.instructor_name}</p>
                  </div>
                  <span className="shrink-0 font-jbmono text-xs font-bold text-[#06B6D4]">{e.progress}%</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/5">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] transition-all duration-500" style={{ width: `${e.progress}%` }} />
                </div>
                <div className="mt-3 space-y-1.5">
                  {e.lessons.map((l, i) => (
                    <button key={i} data-testid={`ac-lesson-${e.id.slice(-6)}-${i}`} onClick={() => toggleLesson(e.id, i)}
                      className="flex w-full items-center gap-2.5 rounded-xl border border-white/5 bg-[#09090B]/60 px-3.5 py-2.5 text-left transition-colors hover:border-white/15">
                      <span className={`flex h-5 w-5 items-center justify-center rounded-md border text-[10px] ${
                        e.done[i] ? "border-[#22C55E] bg-[#22C55E]/20 text-[#22C55E]" : "border-white/15 text-slate-600"}`}>
                        {e.done[i] ? "✓" : i + 1}
                      </span>
                      <span className={`flex-1 text-xs ${e.done[i] ? "text-slate-500 line-through" : "text-slate-200"}`}>{l.title}</span>
                      <span className="font-jbmono text-[9px] text-slate-600">{l.minutes}m</span>
                    </button>
                  ))}
                </div>
                {e.cert_code && (
                  <p className="mt-2.5 rounded-xl bg-[#F97316]/10 px-3.5 py-2 font-jbmono text-[11px] font-bold text-[#F97316]" data-testid={`ac-certcode-${e.id.slice(-6)}`}>
                    🏅 {t(lang, "ac_certificate")}: {e.cert_code}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tab === "teach" && canTeach && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="ac-create">
            <p className="flex items-center gap-1.5 font-display text-sm font-bold"><GraduationCap className="h-4 w-4 text-[#7C3AED]" /> Publish a course</p>
            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <input data-testid="ac-new-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Course title" className={inputCls} />
              <div>
                <input data-testid="ac-new-skill" value={form.skill} onChange={(e) => setForm({ ...form, skill: e.target.value })} placeholder="Skill" list="ac-skills" className={inputCls} />
                <datalist id="ac-skills">{(masters.course_skills || []).map((s) => <option key={s} value={s} />)}</datalist>
              </div>
              <input data-testid="ac-new-price" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price ₹ (0 = free)" className={inputCls} />
              <div className="flex flex-wrap gap-1.5">
                {(masters.course_levels || []).map((l) => (
                  <button key={l} data-testid={`ac-level-${l.toLowerCase()}`} onClick={() => setForm({ ...form, level: l })}
                    className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${form.level === l ? "bg-[#7C3AED] text-white" : "border border-white/10 text-slate-400"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {form.lessons.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`ac-lesson-title-${i}`} value={l.title} onChange={(e) => setForm({ ...form, lessons: form.lessons.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })}
                    placeholder={`Lesson ${i + 1} title`} className={inputCls} />
                  <input data-testid={`ac-lesson-min-${i}`} type="number" min="1" max="300" value={l.minutes} onChange={(e) => setForm({ ...form, lessons: form.lessons.map((x, j) => (j === i ? { ...x, minutes: e.target.value } : x)) })}
                    className="w-24 rounded-2xl border border-white/10 bg-[#09090B]/80 px-3 py-3 text-center font-jbmono text-xs text-white outline-none" />
                  {form.lessons.length > 1 && (
                    <button onClick={() => setForm({ ...form, lessons: form.lessons.filter((_, j) => j !== i) })} className="rounded-full border border-white/10 p-2 text-slate-500 hover:text-white">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button data-testid="ac-lesson-add" onClick={() => setForm({ ...form, lessons: [...form.lessons, { title: "", minutes: 10 }] })}
                className="flex items-center gap-1.5 rounded-full border border-dashed border-[#06B6D4]/40 px-4 py-2 text-[10px] font-bold text-[#06B6D4]">
                <Plus className="h-3 w-3" /> Add lesson
              </button>
            </div>
            <button data-testid="ac-create-submit" onClick={createCourse} disabled={busy === "create" || !form.title || !form.skill || !form.lessons.some((l) => l.title.trim())}
              className="mt-3 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy === "create" ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Publish course"}
            </button>
          </div>
          {mine.map((c) => (
            <div key={c.id} data-testid={`ac-my-${c.id.slice(-6)}`} className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{c.title} <span className={c.published ? "text-[#22C55E]" : "text-slate-500"}>· {c.published ? "live" : "hidden"}</span></p>
                <p className="font-jbmono text-[10px] text-slate-500">{c.lesson_count} lessons · 🎓 {c.enrolled} enrolled · {c.price ? `₹${c.price}` : "Free"}</p>
              </div>
              <button data-testid={`ac-toggle-${c.id.slice(-6)}`} onClick={async () => { await axios.post(`${API}/academy/courses/${c.id}/toggle`, {}, headers()); load(); }}
                className="rounded-full border border-white/15 px-4 py-2 text-xs text-slate-300 hover:text-white">
                {c.published ? "Unpublish" : "Publish"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
